import { SESSION_COOKIE, COOKIE_MAX_AGE } from "@/config/cookies";
import { readClientCookie, writeClientCookie } from "@/shared/helpers/cookies";

/**
 * Formato do id que a nest emite em /auth/session/* (`randomBytes(32)` em
 * hex). A API recusa com 400 "Session ID inválido" qualquer outro valor —
 * inclusive os que o front já inventou no passado (UUID efêmero no SSR,
 * valor fixo em leitura pública).
 */
const SESSION_ID_PATTERN = /^[0-9a-f]{64}$/i;

/** true se o valor tem o formato de um id emitido pela nest. */
export function isValidSessionId(value: unknown): value is string {
    return typeof value === "string" && SESSION_ID_PATTERN.test(value);
}

/** Evita criar duas sessões quando várias requests disparam em paralelo. */
let pendingSession: Promise<string> | null = null;

function persistSession(sessionId: string): void {
    writeClientCookie(SESSION_COOKIE, sessionId, COOKIE_MAX_AGE.year);
}

/** Migra sessões antigas persistidas em localStorage para o cookie. */
function migrateFromLocalStorage(): string | null {
    try {
        const legacy = window.localStorage.getItem(SESSION_COOKIE);
        if (legacy) window.localStorage.removeItem(SESSION_COOKIE);
        if (!isValidSessionId(legacy)) return null;

        persistSession(legacy);
        return legacy;
    } catch {
        return null;
    }
}

async function createBrowserSession(): Promise<string> {
    // Proxy interno (route handler) — injeta o customerIp que o browser não tem
    const response = await fetch("/api/session", { method: "POST" });

    if (!response.ok) throw new Error("Falha ao criar sessão");

    const { sessionId } = (await response.json()) as { sessionId: string };

    // O route handler também envia Set-Cookie; escrever aqui garante o valor
    // imediatamente nesta página, sem depender do próximo request.
    persistSession(sessionId);

    return sessionId;
}

/**
 * SSR: o x-session-id do request, se existir e for válido. `null` no
 * primeiríssimo acesso (ainda sem cookie) e fora do contexto de request
 * (build/testes). NUNCA inventa um id: a nest só aceita os que ela emitiu.
 */
export async function readServerSessionId(): Promise<string | null> {
    try {
        // Import dinâmico: next/headers só existe no runtime do servidor
        const { cookies } = await import("next/headers");
        const value = (await cookies()).get(SESSION_COOKIE)?.value;

        return isValidSessionId(value) ? value : null;
    } catch {
        return null;
    }
}

/**
 * Garante o x-session-id exigido pela API (identifica usuários NÃO
 * autenticados). A sessão vive num cookie de 1 ano, compartilhado entre
 * browser e SSR:
 *
 * - browser: lê o cookie; se não existir ou não tiver o formato emitido pela
 *   nest, cria via POST /api/session (uma única vez) e persiste;
 * - server: lê o mesmo cookie do request via next/headers (`readServerSessionId`);
 *   sem cookie válido, lança — quem roda em SSR deve usar `readServerSessionId`
 *   e omitir o header, nunca mandar um id inventado.
 *
 * Se a criação falhar (API fora), lança — a próxima chamada tenta criar de novo.
 */
export async function ensureSessionId(): Promise<string> {
    if (typeof window === "undefined") {
        const serverId = await readServerSessionId();
        if (serverId) return serverId;
        throw new Error("Request sem x-session-id válido no SSR.");
    }

    const existing = readClientCookie(SESSION_COOKIE);
    if (isValidSessionId(existing)) return existing;

    const migrated = migrateFromLocalStorage();
    if (migrated) return migrated;

    pendingSession ??= createBrowserSession().finally(() => {
        pendingSession = null;
    });

    try {
        return await pendingSession;
    } catch {
        throw new Error(
            "Não foi possível criar uma sessão para este usuário. Verifique se a API de sessão está disponível."
        );
    }
}
